import { useState } from 'react';
import styled from 'styled-components';

import { WoltLogo } from '@creditornot/cb-ingredients';
import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';

import { useFormatMessage } from '../../hooks/useFormatMessage';
import { getResizedImageUrl, ResizedImageWidth } from '../../utils/imageUtils';

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -4px;
  right: -4px;
  height: 24px;
  width: 24px;
  background: ${color.bg};
  border-radius: 50%;

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Root = styled.div<{ $height?: number; $width?: number }>`
  position: relative;
  aspect-ratio: 1 / 1;
  background-color: ${color.bgSurface};
  border: ${dimension.borderWidthRegularMedium} solid ${color.border};
  border-radius: ${dimension.cornerRadiusMedium};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${color.textSubdued};
  flex-shrink: 0;
  height: ${({ $height }) => ($height ? `${$height}px` : undefined)};
  width: ${({ $width }) => ($width ? `${$width}px` : undefined)};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${dimension.cornerRadiusMedium};
`;

const StyledWoltLogo = styled(WoltLogo)`
  fill: ${color.textDisabled};
  margin: 10%;
  max-width: 192px;
`;

type ThumbnailProps = {
  imgSrc?: string | null;
  width?: number;
  height?: number;
  altText?: string;
  className?: string;
  active?: boolean;
  hovered?: boolean;
  icon?: React.ReactElement;
  onClick?: () => void;
};

const getResizedImageWidth = (width: number): ResizedImageWidth => {
  if (width > 960) return 1920;
  if (width > 800) return 1600;
  if (width > 600) return 1200;
  if (width > 480) return 960;
  if (width > 300) return 600;
  if (width > 100) return 300;
  return 200;
};

export function Thumbnail({
  imgSrc,
  width = 48,
  height,
  altText,
  className,
  icon,
  ...rest
}: ThumbnailProps) {
  const [error, setError] = useState(false);
  const f = useFormatMessage();

  return (
    <Root className={className} $width={width} $height={height} {...rest}>
      {!!icon && <IconContainer>{icon}</IconContainer>}
      {imgSrc && !error ? (
        <Image
          src={getResizedImageUrl(imgSrc, getResizedImageWidth(width))}
          alt={altText}
          loading="lazy"
          decoding="async"
          onError={() => setError(true)}
        />
      ) : (
        <StyledWoltLogo aria-label={f('common.not_available_acronym')} />
      )}
    </Root>
  );
}
